import React, { useEffect, useState } from 'react';
import { NavLink, useHistory, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import styled, { css } from 'styled-components/macro';
import {
	closeDrawer,
	logOut,
	logOutReasons,
	openDialog,
	openDrawer,
	trackEvent,
} from 'stash/actions';
import DrawerPortal from 'stash/components/drawer/DrawerPortal';
import { StashLogo } from '@stashinvest/react-assets';
import { BanjoString } from '@stashinvest/react-banjo';

import { getDisabledFeatures } from 'stash/selectors';
import defaultAvatar from 'stash/assets/top-nav/default-avatar.svg';
import { ReactComponent as Refer } from 'stash/assets/top-nav/refer-tab.svg?react';
import { Rail } from '@stashinvest/react-components';
import { track } from 'stash/hooks/useTrack';
import {
	Origin,
	ReferralHome_Clicked,
	Screen,
	UserProfile_Clicked,
} from '@stashinvest/product-events-typescript';

import { isMobileWeb } from 'stash/utils';
import { EXPERIMENTS, useIsInExperimentVariation } from 'stash/components/ab-testing';
import { DownloadBanner } from './DownloadBanner';
import {
	checkIfDownloadBannerIsDismissed,
	storeDownloadBannerDismiss,
} from './DownloadBanner/utils';

type RailNavItems = React.ComponentProps<typeof Rail>['navItems'];

type TopBarUser = {
	first_name?: string;
};

type TopBarOwnProps = {
	hideNav?: boolean;
	navOptions?: unknown[];
	selectedIndex?: number | null;
	onNavLinkClick?: (index?: number) => void;
	settingsUrl?: string;
	stashLogoUrl?: string;
};

type TopBarStateProps = {
	drawerOpen: boolean;
	user: TopBarUser;
	avatar: string;
	disabledFeatures: unknown;
};

type TopBarDispatchProps = {
	open: (e: React.MouseEvent, isHome: boolean) => boolean | void;
	close: () => void;
	logOut: (e: React.MouseEvent) => void;
	goToReferral: (isHome: boolean) => void;
};

type TopBarProps = TopBarOwnProps & TopBarStateProps & TopBarDispatchProps;

const mapStateToProps = (state: any): TopBarStateProps => ({
	drawerOpen: state.application.drawer,
	user: state.entities.user,
	avatar: state.entities.profile.avatar_url || defaultAvatar,
	disabledFeatures: getDisabledFeatures(state),
});

const mapDispatchToProps = (dispatch: any): TopBarDispatchProps => ({
	open: (e, isHome) => {
		const openSideDrawer = () => {
			e.preventDefault();
			dispatch(openDrawer());
		};

		track(
			UserProfile_Clicked.v1({
				origin: Origin.toolbar,
				...(isHome ? { screen: Screen.home } : {}),
			})
		);

		return window.innerWidth > 767 || openSideDrawer();
	},

	close: () => {
		dispatch(closeDrawer());
	},

	logOut: (e) => {
		e.preventDefault();
		return (
			dispatch(
				openDialog({
					prompt: 'Are you sure you want to log out?',
					confirmText: 'Log me out',
					cancelText: 'Cancel',
				})
			) as Promise<unknown>
		)
			.then(() => dispatch(logOut(logOutReasons.LOGGED_OUT)))
			.catch(() => {});
	},

	goToReferral: (isHome) => {
		track(
			ReferralHome_Clicked.v1({
				origin: Origin.toolbar,
				...(isHome ? { screen: Screen.home } : {}),
			})
		);

		dispatch(
			trackEvent('SendReferral', {
				Action: 'ReferralNavHeader',
				Origin: 'WebNavMenu',
			})
		);
	},
});

const NavigationBar = styled.header<{
	isMobile: boolean;
	isDownloadBannerDismissed: boolean;
}>`
	background: rgba(0, 0, 0, 0.3);
	color: ${(p) => p.theme.colors.textPrimary};
	box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.2);
	margin-bottom: 1.5em;
	width: 100%;
	position: fixed;
	top: ${(p) => (p.isDownloadBannerDismissed ? '0' : p.isMobile ? '76px' : '64px')};
	z-index: 11;

	&:before {
		content: '';
		position: absolute;
		background: rgb(var(--background));
		width: 100%;
		height: 100%;
		opacity: 1;
		transition: opacity 0.35s, background 0.2s ease-out;
	}

	&.transparent {
		box-shadow: none;
	}

	&.transparent:before {
		opacity: 0;
	}
`;

const navItemMixin = css`
	text-decoration: none;
	color: ${(p) => p.theme.colors.textPrimary};
	text-rendering: optimizeLegibility;
`;

const TopNavItemLink = styled(NavLink)`
	${navItemMixin}
	display: flex;
	box-sizing: border-box;
	align-items: center;
	min-height: 4rem;
	border-bottom: 2px solid transparent;
	transition: color 0.2s, border-bottom-color 0.2s;
	white-space: nowrap;

	&.active {
		border-bottom-color: ${(p) => p.theme.colors.iconSelected};

		svg {
			color: ${(p) => p.theme.colors.iconSelected};
		}
	}
`;

const StyledUserName = styled.span`
	${navItemMixin}
	margin-left: 0.75rem;
`;

const NavUser = styled.div`
	position: relative;
	cursor: pointer;
`;

const NavUserInfo = styled.div`
	display: flex;
	flex: 1 1 auto;
	align-items: center;
	justify-content: space-between;
`;

const NavUserAvatar = styled.div`
	background-image: url('${defaultAvatar}');
	background-repeat: no-repeat;
	background-position: center;
	background-size: cover;
	width: 1.5rem;
	height: 1.5rem;
	border-radius: 50%;
	text-indent: -9999px;
`;

const TopBar = ({
	open,
	close,
	drawerOpen,
	logOut,
	user,
	avatar,
	hideNav,
	goToReferral,
	navOptions = [],
	selectedIndex,
	onNavLinkClick = () => {},
	settingsUrl = '/account-settings',
	stashLogoUrl = '/',
}: TopBarProps) => {
	const { pathname } = useLocation();
	const history = useHistory();
	const isHome = pathname === '/';
	const isMobile = isMobileWeb();
	const isAppDownloadCardEnabled = useIsInExperimentVariation(
		EXPERIMENTS.WEB_APP_DOWNLOAD_CARD_ENABLED,
		true
	);
	const [isDownloadBannerDismissed, setIsDownloadBannerDismissed] = useState(() =>
		Boolean(checkIfDownloadBannerIsDismissed())
	);
	const hideDownloadBanner = isAppDownloadCardEnabled || isDownloadBannerDismissed;

	const handleDownloadBannerDismiss = () => {
		setIsDownloadBannerDismissed(true);
		storeDownloadBannerDismiss();
	};

	useEffect(() => {
		if (hideDownloadBanner) {
			document.body.classList.add('download-banner-dismissed');
		} else {
			document.body.classList.remove('download-banner-dismissed');
		}
	}, [hideDownloadBanner]);

	return (
		<>
			{!hideDownloadBanner && <DownloadBanner dismiss={handleDownloadBannerDismiss} />}
			<NavigationBar
				className="navigation-bar flex justify-between shrink"
				isMobile={isMobile}
				isDownloadBannerDismissed={Boolean(hideDownloadBanner)}
			>
				<NavLink
					to={stashLogoUrl}
					aria-label="Stash Logo"
					className="relative z-[1] flex shrink px-layout2XS text-brandFill my-contentMedium gridLarge:hidden"
					onClick={() => {
						onNavLinkClick(0);
					}}
				>
					<StashLogo className="flex h-[1.3rem] !pl-0 [&_svg]:h-full [&_svg]:w-auto" />
				</NavLink>
				<Rail
					className="hidden gridLarge:flex shrink z-20 pb-0"
					selectedIndex={selectedIndex ?? -1}
					onIndexSelect={(index) => {
						onNavLinkClick(index);
					}}
					navItems={navOptions as RailNavItems}
					onLogoClick={() => {
						history.push(stashLogoUrl);
					}}
				/>
				<div className="flex shrink gap-layoutXS items-center pr-contentSmall captionSmallEmphasize">
					<NavUser>
						<NavUserInfo className="labelMediumStandard gridLarge:labelSmallStandard gridXL:labelMediumStandard">
							{!hideNav && (
								<TopNavItemLink
									data-testid="refer-a-friend"
									to={{
										pathname: '/referrals',
										state: { origin: Origin?.toolbar },
									}}
									className="!whitespace-normal text-sm mr-layout3XS gridMedium:text-base gridMedium:mr-layout2XS"
									onClick={() => {
										goToReferral(isHome);
										onNavLinkClick(-1);
									}}
								>
									<Refer
										height="1rem"
										width="1rem"
										className="hidden gridMedium:block mr-content2XS"
									/>
									<BanjoString
										data-testid="action/startTask/referAFriend"
										stringId="action/startTask/referAFriend"
									/>
								</TopNavItemLink>
							)}
							<TopNavItemLink
								to={settingsUrl}
								className="justify-center"
								onClick={(e) => {
									onNavLinkClick(-1);
									open(e, isHome);
								}}
								aria-label="Account settings"
							>
								<NavUserAvatar style={{ backgroundImage: `url("${avatar}")` }}>
									User Settings
								</NavUserAvatar>
								<StyledUserName className="hidden gridXL:block">
									{user.first_name || ''}
								</StyledUserName>
							</TopNavItemLink>
						</NavUserInfo>
					</NavUser>
				</div>
			</NavigationBar>

			<DrawerPortal
				active={drawerOpen}
				close={close}
				logOut={logOut as () => void}
				user={user}
				avatar={avatar}
			/>

			<div
				id="top-bar-spacer"
				className={hideDownloadBanner ? 'hidden' : isMobile ? 'pt-[38px]' : 'pt-[10px]'}
			/>
		</>
	);
};

export default connect(mapStateToProps, mapDispatchToProps)(TopBar);
